import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NotificationPreview,
  NotificationRule,
  NotificationRuleInput,
  NotificationRun,
  createNotificationRule,
  deleteNotificationRule,
  disableNotificationRule,
  enableNotificationRule,
  listNotificationRules,
  listNotificationRuns,
  previewNotificationRule,
  runNotificationRule,
  updateNotificationRule,
} from '../api/client';

// ── SVG icons (no external icon library required) ────────────────────────────

function GearIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg className={`h-4 w-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// ── Preset card definitions ───────────────────────────────────────────────────

interface PresetCard {
  key: string;
  icon: string;
  accentColor: string;
  defaultRule: NotificationRuleInput;
}

const VARIABLE_NAMES = ['customer_name', 'invoice_number', 'invoice_amount', 'total_open_debt', 'aged_debt_amount', 'oldest_open_debt_days', 'oldest_open_debt_amount', 'invoice_date'];

const PRESET_CARDS: PresetCard[] = [
  {
    key: 'Old Debt + New Invoice',
    icon: '📄',
    accentColor: 'amber',
    defaultRule: {
      name: 'Old Debt + New Invoice',
      description: 'Alert when a new invoice is issued to a customer who has debt older than 60 days.',
      status: 'enabled',
      interval: 'hourly',
      condition: { event_type: 'invoice_produced', debt_age_days: 60, minimum_debt_amount: 0 },
      message_template: {
        title: 'חשבונית חדשה + חוב ישן',
        body: 'חשבונית {{invoice_number}} הופקה ללקוח {{customer_name}}. סה"כ חוב פתוח: {{total_open_debt}} | חוב ישן: {{aged_debt_amount}} ({{oldest_open_debt_days}} ימים).',
        action_line: 'סכום: {{invoice_amount}} | תאריך: {{invoice_date}}',
      },
    },
  },
  {
    key: 'Invoice with Pending Balance',
    icon: '⚠️',
    accentColor: 'orange',
    defaultRule: {
      name: 'Invoice with Pending Balance',
      description: 'Alert whenever a new invoice is issued to a customer who has any outstanding balance — even a single day old.',
      status: 'enabled',
      interval: 'hourly',
      condition: { event_type: 'invoice_produced', debt_age_days: 1, minimum_debt_amount: 0 },
      message_template: {
        title: 'חשבונית חדשה עם יתרה פתוחה',
        body: 'חשבונית {{invoice_number}} הופקה ללקוח {{customer_name}} עם יתרה פתוחה של {{total_open_debt}}.',
        action_line: 'סכום: {{invoice_amount}} | תאריך: {{invoice_date}}',
      },
    },
  },
  {
    key: 'High-Value Invoice Alert',
    icon: '💰',
    accentColor: 'blue',
    defaultRule: {
      name: 'High-Value Invoice Alert',
      description: 'Alert when a large invoice (₪10,000+) is issued to a customer who already has outstanding debt.',
      status: 'enabled',
      interval: 'hourly',
      condition: {
        event_type: 'invoice_produced',
        debt_age_days: 1,
        minimum_debt_amount: 0,
        filter_conditions: [{ field: 'invoice_amount', operator: '>=', value: 10000 }],
        filter_query: 'invoice_amount >= 10000 AND aged_debt_amount > 0',
      },
      message_template: {
        title: 'חשבונית בסכום גבוה',
        body: 'חשבונית {{invoice_number}} בסכום {{invoice_amount}} הופקה ללקוח {{customer_name}} | סה"כ חוב פתוח: {{total_open_debt}} | חוב ישן: {{aged_debt_amount}}.',
        action_line: 'תאריך: {{invoice_date}}',
      },
    },
  },
  {
    key: 'Chronic Overdue Account',
    icon: '🚨',
    accentColor: 'red',
    defaultRule: {
      name: 'Chronic Overdue Account',
      description: 'Alert when a new invoice is issued to a customer whose debt is 90+ days old and exceeds ₪1,000 — a critical collection risk.',
      status: 'enabled',
      interval: 'hourly',
      condition: { event_type: 'invoice_produced', debt_age_days: 90, minimum_debt_amount: 1000 },
      message_template: {
        title: 'לקוח עם חוב כרוני',
        body: 'חשבונית {{invoice_number}} הופקה ללקוח {{customer_name}} | סה"כ חוב: {{total_open_debt}} | חוב ישן: {{aged_debt_amount}} ({{oldest_open_debt_days}} ימים).',
        action_line: 'סכום החשבונית: {{invoice_amount}} | תאריך: {{invoice_date}}',
      },
    },
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(value?: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

function toInput(rule: NotificationRule): NotificationRuleInput {
  return {
    name: rule.name,
    description: rule.description ?? '',
    status: rule.status,
    interval: 'hourly',
    condition: rule.condition,
    message_template: rule.message_template,
    ai_generated: rule.ai_generated,
    ai_prompt: rule.ai_prompt,
  };
}

const accentClasses: Record<string, { border: string; bg: string; badge: string }> = {
  amber: { border: 'border-amber-200', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-800' },
  orange: { border: 'border-orange-200', bg: 'bg-orange-50', badge: 'bg-orange-100 text-orange-800' },
  blue: { border: 'border-blue-200', bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-800' },
  red: { border: 'border-red-200', bg: 'bg-red-50', badge: 'bg-red-100 text-red-800' },
};

// ── NotificationCard component ────────────────────────────────────────────────

interface CardProps {
  preset: PresetCard;
  rule: NotificationRule | null;
  runs: NotificationRun[];
  onRuleChange: () => void;
}

function NotificationCard({ preset, rule, runs, onRuleChange }: CardProps) {
  const { t } = useTranslation();
  const isCustomNew = rule === null && !preset.defaultRule.name;
  const [expanded, setExpanded] = useState(isCustomNew);
  const [draft, setDraft] = useState<NotificationRuleInput>(rule ? toInput(rule) : preset.defaultRule);
  const [preview, setPreview] = useState<NotificationPreview | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const accent = accentClasses[preset.accentColor] ?? accentClasses.amber;
  const isEnabled = rule?.status === 'enabled';
  const isNew = rule === null;

  const setDraftField = (path: string, value: string | number) => {
    setDraft((current) => {
      const next: any = structuredClone(current);
      const parts = path.split('.');
      let target = next;
      for (const part of parts.slice(0, -1)) target = target[part];
      target[parts[parts.length - 1]] = value;
      return next;
    });
    setPreview(null);
  };

  const insertVariable = (field: 'body' | 'action_line', name: string) => {
    const value = `${draft.message_template[field] || ''} {{${name}}}`;
    setDraftField(`message_template.${field}`, value.trim());
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      if (rule) {
        await updateNotificationRule(rule.id, draft);
      } else {
        await createNotificationRule({ ...draft, status: 'enabled' });
      }
      setNotice(rule ? t('notifications.updated') : t('notifications.created'));
      onRuleChange();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('notifications.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const doPreview = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await previewNotificationRule(rule ? { rule_id: rule.id } : { rule: draft });
      setPreview(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('notifications.errors.preview'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async () => {
    if (!rule) return;
    setSaving(true);
    setError('');
    try {
      if (isEnabled) {
        await disableNotificationRule(rule.id);
      } else {
        await enableNotificationRule(rule.id);
      }
      onRuleChange();
    } catch (err: any) {
      setError(err.response?.data?.detail || (isEnabled ? t('notifications.errors.disable') : t('notifications.errors.enable')));
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (!rule) return;
    setSaving(true);
    setError('');
    try {
      await runNotificationRule(rule.id);
      onRuleChange();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('notifications.errors.run'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!rule || !window.confirm(t('notifications.deleteConfirm'))) return;
    setSaving(true);
    try {
      await deleteNotificationRule(rule.id);
      onRuleChange();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('notifications.errors.delete'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-xl border-2 bg-white shadow-sm transition-shadow hover:shadow-md ${isNew ? 'border-dashed border-gray-200' : accent.border}`}>
      {/* ── Card header ─────────────────────────────────────── */}
      <div className="flex items-start gap-4 p-5">
        <span className="mt-0.5 text-3xl" role="img" aria-label={preset.key}>{preset.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-gray-900">{draft.name || preset.key || t('notifications.newRule', 'New Rule')}</h2>
            {isNew ? (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">{t('notifications.statusOff', 'Off - tap to set up')}</span>
            ) : isEnabled ? (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">{t('notifications.statusEnabled', 'Enabled')}</span>
            ) : (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{t('notifications.statusDisabled', 'Disabled')}</span>
            )}
            {rule?.ai_generated && (
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${accent.badge}`}>AI</span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500 leading-relaxed">{draft.description || preset.defaultRule.description}</p>
          {rule && (
            <p className="mt-2 text-xs text-gray-400">
              Last run: {formatDate(rule.last_run_at)} · Next: {formatDate(rule.next_run_at)}
            </p>
          )}
        </div>

        {/* ── Right-side actions ───────────────────────────── */}
        <div className="flex shrink-0 items-center gap-2">
          {rule && (
            <>
              {/* Enable / Disable toggle */}
              <button
                onClick={toggle}
                disabled={saving}
                title={isEnabled ? 'Disable' : 'Enable'}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${isEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <button onClick={runNow} disabled={saving} title="Run now" className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                Run
              </button>
            </>
          )}
          {/* Settings gear */}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-800"
            title="Configure"
          >
            <GearIcon className="h-4 w-4" />
          </button>
          <ChevronDown open={expanded} />
        </div>
      </div>

      {/* ── Expanded settings panel ──────────────────────── */}
      {expanded && (
        <div className={`border-t border-gray-100 ${accent.bg} px-5 py-5`}>
          <h3 className="mb-4 text-sm font-semibold text-gray-700 uppercase tracking-wide">Configuration</h3>

          {(error || notice) && (
            <div className={`mb-4 rounded-md border px-3 py-2 text-sm ${error ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
              {error || notice}
            </div>
          )}

          <div className="space-y-3">
            <label className="block text-sm">
              <span className="font-medium text-gray-700">{t('notifications.ruleName')}</span>
              <input
                value={draft.name}
                onChange={(e) => setDraftField('name', e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              />
            </label>

            <label className="block text-sm">
              <span className="font-medium text-gray-700">Description</span>
              <input
                value={draft.description ?? ''}
                onChange={(e) => setDraftField('description', e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="font-medium text-gray-700">{t('notifications.debtAgeDays')}</span>
                <input
                  type="number" min={1}
                  value={draft.condition.debt_age_days}
                  onChange={(e) => setDraftField('condition.debt_age_days', Number(e.target.value))}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-gray-700">{t('notifications.minimumDebt')}</span>
                <input
                  type="number" min={0}
                  value={draft.condition.minimum_debt_amount}
                  onChange={(e) => setDraftField('condition.minimum_debt_amount', Number(e.target.value))}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                />
              </label>
            </div>

            {/* AI filter query (editable for AI-generated rules or advanced users) */}
            {draft.condition.filter_query !== undefined && (
              <label className="block text-sm">
                <span className="font-medium text-gray-700">Filter query <span className="text-xs text-gray-400">(AI-authored logic)</span></span>
                <input
                  value={draft.condition.filter_query ?? ''}
                  onChange={(e) => setDraftField('condition.filter_query', e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono"
                />
              </label>
            )}

            <label className="block text-sm">
              <span className="font-medium text-gray-700">{t('notifications.templateTitle')}</span>
              <input
                value={draft.message_template.title}
                onChange={(e) => setDraftField('message_template.title', e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              />
            </label>

            <label className="block text-sm">
              <span className="font-medium text-gray-700">{t('notifications.messageBody')}</span>
              <textarea
                value={draft.message_template.body}
                onChange={(e) => setDraftField('message_template.body', e.target.value)}
                className="mt-1 min-h-24 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              />
            </label>

            <label className="block text-sm">
              <span className="font-medium text-gray-700">{t('notifications.referenceLine')}</span>
              <textarea
                value={draft.message_template.action_line ?? ''}
                onChange={(e) => setDraftField('message_template.action_line', e.target.value)}
                className="mt-1 min-h-14 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              />
            </label>

            {/* Variable chips */}
            <div className="flex flex-wrap gap-2">
              {VARIABLE_NAMES.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => insertVariable('body', name)}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                >
                  {`{{${name}}}`}
                </button>
              ))}
            </div>

            {/* Action row */}
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                disabled={saving}
                onClick={save}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {isNew ? 'Enable' : t('common.save')}
              </button>
              <button
                disabled={saving}
                onClick={doPreview}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('notifications.preview')}
              </button>
              {rule && (
                <button
                  disabled={saving}
                  onClick={doDelete}
                  className="ml-auto rounded-md border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {t('common.delete')}
                </button>
              )}
            </div>
          </div>

          {/* Preview results */}
          {preview && (
            <div className="mt-4 rounded-md border border-gray-200 bg-white p-4">
              <p className="text-sm font-medium text-gray-700">{t('notifications.previewCounts', { matches: preview.matched_count, recipients: preview.recipient_count })}</p>
              <div className="mt-2 rounded-md bg-gray-50 p-3 text-sm text-gray-700 whitespace-pre-wrap">{preview.message_preview || t('notifications.noMatches')}</div>
              {preview.sample_events.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {preview.sample_events.map((ev) => (
                    <li key={ev.event_key} className="rounded-md border border-gray-100 p-2 text-sm">
                      <span className="font-medium text-gray-900">{ev.customer_name}</span>
                      <span className="block text-gray-500">{t('notifications.previewEvent', { invoice: ev.invoice_number, amount: (ev.total_open_debt ?? ev.aged_debt_amount).toLocaleString(), days: ev.oldest_open_debt_days })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Run history */}
          {runs.length > 0 && (
            <div className="mt-4">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{t('notifications.runHistory')}</h4>
              <div className="space-y-2">
                {runs.slice(0, 5).map((run) => (
                  <div key={run.id} className="rounded-md border border-gray-100 bg-white p-3 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="font-medium text-gray-900">{run.status}</span>
                      <span className="text-gray-400">{formatDate(run.started_at)}</span>
                    </div>
                    <p className="mt-1 text-gray-500">
                      {t('notifications.runCounts', {
                        matched: run.matched_count,
                        duplicates: run.skipped_duplicate_count,
                        sent: run.action_succeeded_count,
                        failed: run.action_failed_count,
                      })}
                    </p>
                    {run.error_summary && <p className="mt-1 text-red-600">{run.error_summary}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Notifications() {
  const { t } = useTranslation();
  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [runsMap, setRunsMap] = useState<Record<string, NotificationRun[]>>({});
  const [loading, setLoading] = useState(true);
  const [globalError, setGlobalError] = useState('');

  const load = async () => {
    setLoading(true);
    setGlobalError('');
    try {
      const response = await listNotificationRules();
      const fetched = response.data.items;
      setRules(fetched);

      // Load run history for each rule in parallel
      const entries = await Promise.all(
        fetched.map(async (r) => {
          try {
            const res = await listNotificationRuns(r.id);
            return [r.id, res.data.items] as [string, NotificationRun[]];
          } catch {
            return [r.id, []] as [string, NotificationRun[]];
          }
        }),
      );
      setRunsMap(Object.fromEntries(entries));
    } catch (err: any) {
      setGlobalError(err.response?.data?.detail || t('notifications.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Extra rules that exist in DB but don't match any preset (custom / AI-generated)
  const presetNames = new Set(PRESET_CARDS.map((p) => p.key));
  const extraRules = rules.filter((r) => !presetNames.has(r.name));

  // State for adding a new custom rule
  const [showNewCustom, setShowNewCustom] = useState(false);

  const newCustomPreset: PresetCard = {
    key: 'New Custom Rule',
    icon: '🔔',
    accentColor: 'amber',
    defaultRule: {
      name: '',
      description: '',
      status: 'enabled',
      interval: 'hourly',
      condition: { event_type: 'invoice_produced', debt_age_days: 30, minimum_debt_amount: 0 },
      message_template: { title: '', body: '', action_line: '' },
    },
  };

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('notifications.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('notifications.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNewCustom(true)}
            className="shrink-0 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            {t('notifications.addRule', '+ New Rule')}
          </button>
          <button onClick={load} className="shrink-0 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Refresh
          </button>
        </div>
      </header>

      {globalError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{globalError}</div>
      )}

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-400">{t('common.loading')}</div>
      ) : (
        <>
          {/* Preset notification cards */}
          <div className="grid gap-4 md:grid-cols-2">
            {PRESET_CARDS.map((preset) => {
              const rule = rules.find((r) => r.name === preset.key) ?? null;
              return (
                <NotificationCard
                  key={preset.key}
                  preset={preset}
                  rule={rule}
                  runs={rule ? (runsMap[rule.id] ?? []) : []}
                  onRuleChange={load}
                />
              );
            })}
          </div>

          {/* Custom / AI-generated rules that don't match a preset */}
          {(extraRules.length > 0 || showNewCustom) && (
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Custom Rules</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {showNewCustom && (
                  <NotificationCard
                    key="__new_custom__"
                    preset={newCustomPreset}
                    rule={null}
                    runs={[]}
                    onRuleChange={() => { setShowNewCustom(false); load(); }}
                  />
                )}
                {extraRules.map((rule) => (
                  <NotificationCard
                    key={rule.id}
                    preset={{
                      key: rule.name,
                      icon: rule.ai_generated ? '🤖' : '🔔',
                      accentColor: rule.ai_generated ? 'blue' : 'amber',
                      defaultRule: toInput(rule),
                    }}
                    rule={rule}
                    runs={runsMap[rule.id] ?? []}
                    onRuleChange={load}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
